import { ToggleButtonGroup } from 'app/components/Common/ToggleButtonGroup';
import { debounce, ToggleButton, Grid } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormInputText } from '../../../PersonalInfoTab/Form/Controls/FormInputText';
import { isWithinInterval, isAfter, isBefore } from 'date-fns';
import { FormInputDatePicker } from '../../../PersonalInfoTab/Form/Controls/FormInputDatePicker';
import { ITypeAheadOption } from '../../../PreferencesTab/CustomComponents/ControlledTypeAhead';
import {  getLocationStatesCitiesZip } from 'app/services/SharedServices/SharedServices';
import { FormInputCheckbox } from '../../../PersonalInfoTab/Form/Controls/FormInputCheckbox';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { candidateDetailsSelection } from '../../../../Profile/CandidateDetails.selector';
import { useSelector } from 'react-redux';
import { ff_ui_candidate_manage_EquipmentProcedure } from 'app/constants/FeatureFlags/Candidate/Keys';
import { useDecision } from '@optimizely/react-sdk';
import { TypeAhead } from '@AMIEWEB/Common';
import statesData from 'app/assets/jsons/State.json';
import { IOption } from '../../../ProfileTab/Education/FormInputDropdown';
import FormInputTextField from '../../../../WorkExperience/Edit/components/FormInputTextField';
import { theme } from 'styles/global-styles';
import { GenericDialogButton } from '@AMIEWEB/Alerts/GenericDialog';
import { addWorkExperienceFormStyles } from './AddWorkExperience.styles';

enum CountryCodes {
  USA = 1,
  CANADA = 2,
}

export const AddWorkExperienceForm = props => {
  const { classes } = addWorkExperienceFormStyles();
  const { t } = useTranslation();
  const candidateDetails = useSelector(candidateDetailsSelection);
  const [filteredSpecialty, setFilteredSpecialty] = React.useState<ITypeAheadOption[]>([]);
  const [candidateManageEquipmentProcedure] = useDecision(ff_ui_candidate_manage_EquipmentProcedure);
  const [pSupervisor, setPSupervisor] = useState<boolean>(false);
  const [pTeletherapy, setPTeletherapy] = useState<boolean>(false);
  const [pHospital, setPHospital] = useState<boolean>(false);
  const [pOngoing, setPOngoing] = useState<boolean>(false);
  const [addWorkExperience, setAddWorkExperience] = useState<boolean>(true);
  const [errorMessageOpen, setErrorMessageOpen] = useState<boolean>(false);
  const [enableSaveOnOverlap, setEnableSaveOnOverlap] = useState<boolean>(true);
  const [startValue, setStartValue] = useState<Date>(null);
  const [endValue, setEndValue] = useState<Date>(null);
  const [addTimeOff, setAddTimeOff] = useState<boolean>(false);
  const { formState: { errors }, getValues, setValue, clearErrors, control, trigger, setError, formState, watch } = useFormContext();
  const [charCount, setCharCount] = useState<number>(0);
  const [cityOptions, setCityOptions] = useState<IOption[]>([]);
  const [statesOptions, setStatesOptions] = useState<IOption[]>([]);
  const [defaultCities, setDefaultCities] = useState<IOption[]>([]);

  const facilityEmployer = watch('pFacilityEmployer');
  const discipline = watch('piDiscipline');
  const speciality = watch('piSpecialty');
  const selectedState = watch('pState');
  const selectedCity = watch('piCity');
  const onGoing = watch('pOngoing');
  const selectedCountry = watch('pCountry');
  const zipCode = watch('piZip');
  const selectedWorkHistoryType = watch('pWorkHistoryType');
  const workHistoryTypeOptions = props.typeAheadOptions?.workHistoryTypes || [];
  const workHistoryTypeOptionsForWorkExperience = workHistoryTypeOptions.filter(option => option.label !== 'Time Off');

  useEffect(() => {
    if (!getValues('pAddWorkExperience') && addWorkExperience) {
      setValue('pAddTimeOff', false);
      setValue('pAddWorkExperience', addWorkExperience);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartDateChange = event => {
    setValue('pStart', event, { shouldDirty: true });
    setStartValue(new Date(event));
    clearErrors('pStart');
    setEnableSaveOnOverlap(true);
  };

  useEffect(() => {
    const endFromDate = endValue ? new Date(endValue) : null;
    const startDate = startValue ? new Date(startValue) : null;

    if (hasDateOverlapping()) {
      setErrorMessageOpen(true);
    } else {
      setErrorMessageOpen(false);
    }

    const currentlyWorking = getValues('currentlyWorking');
    if (!currentlyWorking && !pOngoing && endFromDate?.getTime() < startDate?.getTime()) {
      setError('pEnd', {
        type: 'validate',
        message: 'End date must be greater than start date',
      });
    } else {
      clearErrors('pEnd');
    }
    if (startDate?.getTime() > endFromDate?.getTime()) {
      setError('pStart', {
        type: 'validate',
        message: 'Start date must be less than end date',
      });
    } else {
      clearErrors('pStart');
    }
  }, [startValue, endValue]);

  const handleEndDateChange = event => {
    setValue('pEnd', event, { shouldDirty: true });
    setEndValue(new Date(event));
    clearErrors('pEnd');
    setEnableSaveOnOverlap(true);
  };

  const handlePNotes = event => {
    setValue('pnotes', event.target.value, { shouldDirty: true });
    setCharCount(event.target.value?.length);
  };

  const hasDateOverlapping = () => {
    const endFromDate = endValue ? new Date(endValue) : null;
    const startDate = startValue ? new Date(startValue) : null;

    if (addTimeOff) {
      if (isOverlappingDates(startDate, endFromDate)) {
        return true;
      } else {
        return false;
      }
    }
  };

  useEffect(() => {
    if (discipline) {
      const filteredData = props.typeAheadOptions?.specialties.filter(data => {
        return data.object.DisciplineID === discipline.object.ID;
      });
      filteredData.sort((a, b) => a.label?.localeCompare(b.label));
      setFilteredSpecialty(filteredData);
      filteredData.length && setValue('piSpecialty', filteredData[0], { shouldDirty: true });
    }
  }, [discipline]);

  const isOverlappingDates = (startDate: Date, endDate: Date) => {
    let isOverlapping = false;
    candidateDetails?.experience?.employment.map(item => {
      const startDateOn = new Date(item.startedOn);
      const endDateOn = item.endedOn ? new Date(item.endedOn) : null;

      if (
        endDateOn &&
        startDateOn <= endDateOn &&
        (isWithinInterval(startDate as Date, {
          start: startDateOn,
          end: endDateOn,
        }) ||
          isWithinInterval(endDate as Date, {
            start: startDateOn,
            end: endDateOn,
          }))
      ) {
        isOverlapping = true;
      } else if (!endDateOn && (isAfter(startDate, startDateOn) || isAfter(endDate, startDateOn))) {
        isOverlapping = true;
      } else if (endDateOn && isBefore(startDate, startDateOn) && isAfter(endDate, new Date(item.endedOn))) {
        isOverlapping = true;
      }
    });
    return isOverlapping;
  };

  const handleCurrentlyWorkingChange = event => {
    const isChecked = event;

    if (isChecked) {
      errors['pReason'] = '';
    }

    setValue('currentlyWorking', isChecked, { shouldDirty: true });
  };

  useEffect(() => {
    const country = getValues('pCountry');
    if (country?.id === CountryCodes.USA || country?.id === CountryCodes.CANADA) {
      const states = statesData.states
        .filter(e => e.CountryID === country?.id)
        .map(e => ({ id: e.ID, label: e.Description, value: e.StateNumber }));

      setStatesOptions(states);
      if (addWorkExperience) {
        setValue('pState', states[0]);
      } else {
        setValue('pState', '');
        setValue('piCity', '');
      }
    } else if (formState['dirtyFields']['pCountry'] || formState['touchedFields']['pCountry']) {
      setStatesOptions([]);

      // DO NOT REMOVE SetTimeout otherwise state wont reset once you change state from USA to non-USA.
      setTimeout(() => {
        setValue('pState', '');
      }, 0);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedCountry?.id === CountryCodes.USA && selectedState) {
      fetchCities();
    } else {
      setCityOptions([]);
    }

    setTimeout(() => {
      setValue('piCity', '');
      trigger(['piCity']);
    }, 0);
  }, [selectedState]);

  const fetchCities = async () => {
    const selectedStates = typeof selectedState ? [selectedState?.label] : [];
    const result = getLocationStatesCitiesZip({
      states: selectedStates,
      cities: [],
    });

    let filteredCities = [];
    result.then(res => {
      filteredCities = res[0]?.cities?.map(item => {
        return { id: item.id, label: item.name };
      });

      setCityOptions(filteredCities);
      setDefaultCities(filteredCities);
    });
  };

  const onTextSearch = async (key: string, searchMode: 'c' | 'z') => {
    const selectedStates = selectedState ? [selectedState?.label] : [];
    if (searchMode === 'c') {
      const result = getLocationStatesCitiesZip({
        states: selectedStates,
        cities: [],
        searchMode: searchMode,
        searchValue: key,
      });

      let filteredCities = [];
      result.then(res => {
        filteredCities = res[0]?.cities?.map(item => {
          return { id: item.id, label: item.name };
        });

        setCityOptions(filteredCities);
      });
    }
  };

  const yesAction = () => {
    setErrorMessageOpen(false);
    props.setSaveDisabled(false);
    setEnableSaveOnOverlap(true);
  };

  const enableSaveOnChanges = () => {
    const values = getValues();
    const { pFacilityEmployer, piDiscipline, piSpecialty, pState, piCity, pStart, pCountry, piZip, pWorkHistoryType } =
      values;

    if (addWorkExperience) {
      if (!pFacilityEmployer) {
        props.setSaveDisabled(true);
        return;
      } else if (!pWorkHistoryType) {
        props.setSaveDisabled(true);
        return;
      } else if (!piDiscipline) {
        props.setSaveDisabled(true);
        return;
      } else if (!piSpecialty) {
        props.setSaveDisabled(true);
        return;
      } else if (!pState && (pCountry?.id === CountryCodes.USA || pCountry?.id === CountryCodes.CANADA)) {
        props.setSaveDisabled(true);
        return;
      } else if (!piCity && pCountry?.id === CountryCodes.USA) {
        props.setSaveDisabled(true);
        return;
      } else if (!pStart) {
        props.setSaveDisabled(true);
        return;
      } else {
        props.setSaveDisabled(false);
        return;
      }
    } else {
      if (!pStart) {
        props.setSaveDisabled(true);
        return;
      } else if (errorMessageOpen) {
        props.setSaveDisabled(true);
        return;
      } else if (!!pState && !piCity && pCountry?.id === CountryCodes.USA) {
        props.setSaveDisabled(true);
        return;
      } else if (
        piZip?.length > 5 ||
        (!(pCountry?.id === CountryCodes.USA || pCountry?.id === CountryCodes.CANADA) && pState?.length > 100) ||
        (pCountry?.id !== CountryCodes.USA && piCity?.length > 100)
      ) {
        props.setSaveDisabled(true);
        return;
      } else {
        if (hasDateOverlapping()) {
          if (enableSaveOnOverlap) {
            props.setSaveDisabled(false);
          } else {
            props.setSaveDisabled(true);
          }
        } else {
          props.setSaveDisabled(false);
        }
        return;
      }
    }
  };

  useEffect(() => {
    enableSaveOnChanges();
  }, [
    facilityEmployer,
    discipline,
    speciality,
    selectedWorkHistoryType,
    selectedState,
    selectedCity,
    startValue,
    endValue,
    onGoing,
    zipCode,
    errorMessageOpen,
    selectedCity,
    selectedState,
  ]);

  return (
    <>
      <div className={classes.contentContainer}>
        <Grid
          id="generic-dialog-content-heading"
          className={classes.heading}
          style={{ marginBottom: '20px', borderBottomLeftRadius: '0px', borderBottomRightRadius: '0px' }}
        >
          <span>
            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{'Work Experience '}</span>
            <span style={{ fontSize: '14px' }}>{'*Required Fields'}</span>
          </span>
        </Grid>

        <LayoutGrid container direction="column" className={classes.root} spacing={4}>
          <LayoutGridItem container display="flex" flexDirection="row">
            <LayoutGridItem xs={8.35}>
              <Controller
                name="pAddWorkExperience"
                key={'Work Experience'}
                control={control}
                render={({ field: { ref, value, onChange, ...rest } }) => (
                  <ToggleButtonGroup
                    color="secondary"
                    value={addWorkExperience ? 'workExperience' : ''}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setAddWorkExperience(newValue === 'workExperience');
                        setAddTimeOff(false);
                        setValue('pAddTimeOff', false);
                        setValue('pAddWorkExperience', newValue === 'workExperience');
                        if (newValue === 'workExperience' && getValues('pWorkHistoryType')?.label === 'Time Off') {
                          setValue('pWorkHistoryType', null, { shouldDirty: true });
                        }
                      }
                    }}
                  >
                    <ToggleButton
                      style={{
                        width: theme.spacing(14),
                        borderTopRightRadius: '0px',
                        borderBottomRightRadius: '0px',
                        textTransform: 'none',
                      }}
                      value="workExperience"
                    >{`Travel`}</ToggleButton>
                  </ToggleButtonGroup>
                )}
              />

              <Controller
                key={'Time Off'}
                name="pAddTimeOff"
                control={control}
                render={({ field: { ref, value, onChange, ...rest } }) => (
                  <ToggleButtonGroup
                    color="secondary"
                    value={addTimeOff ? 'timeOff' : ''}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setAddTimeOff(newValue === 'timeOff');
                        setAddWorkExperience(false);
                        setValue('pAddTimeOff', newValue === 'timeOff');
                        setValue('pAddWorkExperience', false);
                        const timeOffOption = workHistoryTypeOptions.find(option => option.label === 'Time Off');
                        setValue('pWorkHistoryType', timeOffOption || null, { shouldDirty: true });
                      }
                    }}
                  >
                    <ToggleButton
                      style={{
                        width: theme.spacing(16),
                        borderTopLeftRadius: '0px',
                        borderBottomLeftRadius: '0px',
                        textTransform: 'none',
                      }}
                      value="timeOff"
                    >
                      Time Off
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </LayoutGridItem>
            {addWorkExperience && (
              <LayoutGridItem>
                <FormInputCheckbox
                  name={`currentlyWorking`}
                  handleChecked={e => handleCurrentlyWorkingChange(e)}
                  isChecked={!!watch('currentlyWorking')}
                  checkboxLabel={'Current Employer'}
                ></FormInputCheckbox>
              </LayoutGridItem>
            )}
          </LayoutGridItem>

          {addWorkExperience ? (
            <>
              <LayoutGridItem item>
                <LayoutGrid
                  container
                  direction="row"
                  data-testid="container"
                  justifyContent="flex-start"
                  spacing={6}
                  sx={theme => ({ marginBottom: theme.spacing(9) })}
                >
                  <LayoutGridItem item xs={5}>
                    <LayoutGridItem container direction="column" spacing={4}>
                      <LayoutGridItem container>
                        <LayoutGridItem item xs={9}>
                          <Controller
                            name="pWorkHistoryType"
                            defaultValue={null}
                            control={control}
                            render={({ field: { ref, ...rest } }) => (
                              <TypeAhead
                                options={workHistoryTypeOptionsForWorkExperience}
                                label="Work History Type *"
                                {...rest}
                                isError={!!errors['pWorkHistoryType']}
                              />
                            )}
                          />
                        </LayoutGridItem>
                      </LayoutGridItem>
                      <LayoutGridItem container>
                        <LayoutGridItem item xs={12}>
                          <FormInputText
                            name="pFacilityEmployer"
                            label={'Facility/Employer *'}
                            message={errors['pFacilityEmployer']?.message || ''}
                          />
                        </LayoutGridItem>
                      </LayoutGridItem>
                      <LayoutGridItem container>
                        <LayoutGridItem item xs={9}>
                          <Controller
                            name="pCountry"
                            defaultValue={null}
                            control={control}
                            render={({ field: { ref, ...rest } }) => (
                              <TypeAhead
                                options={props.countriesData}
                                label="Country"
                                {...rest}
                                isError={!!errors['pCountry']}
                              />
                            )}
                          />
                        </LayoutGridItem>
                      </LayoutGridItem>
                      <LayoutGridItem container display="flex" direction="row" spacing={2}>
                        <LayoutGridItem item xs={5}>
                          {watch('pCountry')?.id === CountryCodes.USA ||
                          watch('pCountry')?.id === CountryCodes.CANADA ? (
                            <Controller
                              name="pState"
                              defaultValue={statesOptions[0] || null}
                              control={control}
                              render={({ field: { ref, ...rest } }) => (
                                <TypeAhead
                                  options={statesOptions || []}
                                  label="State *"
                                  {...rest}
                                  isError={!!errors['pState']}
                                />
                              )}
                            />
                          ) : (
                            <FormInputTextField name="pState" label={'State'} />
                          )}
                        </LayoutGridItem>
                        <LayoutGridItem item xs={4}>
                          {selectedCountry?.id === CountryCodes.USA ? (
                            <Controller
                              name="piCity"
                              defaultValue={null}
                              control={control}
                              render={({ field: { ref, ...rest } }) => (
                                <TypeAhead
                                  options={cityOptions || []}
                                  label="City *"
                                  {...rest}
                                  onInputChange={(text, reason) =>
                                    reason !== 'reset' && debounce(text => onTextSearch(text, 'c'), 1500)(text)
                                  }
                                  onFocus={() => {
                                    const cityOpts =
                                      !!rest.value && !defaultCities.some(item => item.id === rest.value.id)
                                        ? [rest.value, ...defaultCities]
                                        : defaultCities;
                                    setCityOptions(cityOpts);
                                  }}
                                />
                              )}
                            />
                          ) : (
                            <FormInputTextField name="piCity" label={'City'} required={false} />
                          )}
                        </LayoutGridItem>
                        <LayoutGridItem item xs={3}>
                          <FormInputText name="piZip" label={'Zip Code'} message={errors['piZip']?.message || ''} />
                        </LayoutGridItem>
                      </LayoutGridItem>
                      <LayoutGridItem container display="flex" direction="row" spacing={2}>
                        <LayoutGridItem item xs={5}>
                          <FormInputCheckbox
                            name={`pSupervisor`}
                            isChecked={pSupervisor}
                            handleChecked={() => {
                              setPSupervisor(!pSupervisor);
                            }}
                            checkboxLabel={`Supervisor/Charge`}
                            isDisabled={false}
                            checkboxErrorName={`CheckboxError pSupervisor`}
                          ></FormInputCheckbox>
                        </LayoutGridItem>
                        <LayoutGridItem item>
                          <FormInputCheckbox
                            name={`pTeletherapy`}
                            isChecked={pTeletherapy}
                            handleChecked={() => {
                              setPTeletherapy(!pTeletherapy);
                            }}
                            checkboxLabel={`Telehealth/Teletherapy`}
                            isDisabled={false}
                            checkboxErrorName={`CheckboxError pTeletherapy`}
                          ></FormInputCheckbox>
                        </LayoutGridItem>
                      </LayoutGridItem>
                      <LayoutGridItem container>
                        <LayoutGridItem item xs={5} pt={'10px'}>
                          <FormInputCheckbox
                            name={`pHospital`}
                            isChecked={pHospital}
                            handleChecked={() => {
                              setPHospital(!pHospital);
                            }}
                            checkboxLabel={`Teaching Hospital`}
                            isDisabled={false}
                            checkboxErrorName={`CheckboxError pHospital`}
                          ></FormInputCheckbox>
                        </LayoutGridItem>
                      </LayoutGridItem>
                    </LayoutGridItem>
                  </LayoutGridItem>
                  <LayoutGridItem item xs={3}>
                    <LayoutGridItem container direction="column" spacing={4}>
                      <LayoutGridItem container>
                        <LayoutGridItem item xs={12}>
                          <Controller
                            name="piTravelerCompany"
                            defaultValue={null}
                            control={control}
                            render={({ field: { ref, ...rest } }) => (
                              <TypeAhead
                                options={props.travelCompanyData || []}
                                label="Staffing Agency"
                                {...rest}
                                isError={!!errors['piTravelerCompany']}
                              />
                            )}
                          />
                        </LayoutGridItem>
                      </LayoutGridItem>
                      <LayoutGridItem container>
                        <LayoutGridItem item xs={12}>
                          <Controller
                            name="piDiscipline"
                            control={control}
                            render={({ field: { ref, ...rest } }) => (
                              <TypeAhead
                                options={props.typeAheadOptions?.disciplines || []}
                                label="Discipline *"
                                {...rest}
                                isError={!!errors.piDiscipline}
                              />
                            )}
                          />
                        </LayoutGridItem>
                      </LayoutGridItem>
                      <LayoutGridItem container>
                        <LayoutGridItem item xs={12}>
                          <Controller
                            name="piSpecialty"
                            control={control}
                            defaultValue={getValues('piSpecialty') || {}}
                            render={({ field: { ref, ...rest } }) => (
                              <TypeAhead
                                options={filteredSpecialty.length ? filteredSpecialty : []}
                                label="Specialty *"
                                {...rest}
                                isError={!!errors['piSpecialty']}
                              />
                            )}
                          />
                        </LayoutGridItem>
                      </LayoutGridItem>
                      <LayoutGridItem container>
                        <LayoutGridItem item xs={9}>
                          <FormInputText
                            name="pBeds"
                            label={'# of Beds in Facility'}
                            require={false}
                            type={'number'}
                            message={errors['pBeds']?.message || ''}
                          />
                        </LayoutGridItem>
                      </LayoutGridItem>
                      <LayoutGridItem container>
                        <LayoutGridItem item xs={12}>
                          <FormInputText
                            name="pUnitFloorDept"
                            label={'Unit/Floor/Dept'}
                            require={false}
                            message={errors['pUnitFloorDept']?.message || ''}
                          />
                        </LayoutGridItem>
                      </LayoutGridItem>
                      <LayoutGridItem container>
                        <LayoutGridItem item xs={12}>
                          <FormInputText
                            name="pPosition"
                            label={'Position Held'}
                            require={false}
                            message={errors['pPosition']?.message || ''}
                          />
                        </LayoutGridItem>
                      </LayoutGridItem>
                    </LayoutGridItem>
                  </LayoutGridItem>
                  <LayoutGridItem item xs={4}>
                    <LayoutGridItem container direction="column" spacing={4}>
                      <LayoutGridItem container display="flex" direction="row" xs={4} spacing={2}>
                        <LayoutGridItem item xs={5}>
                          <FormInputDatePicker
                            name={'pStart'}
                            label={'Start Date *'}
                            handleChange={handleStartDateChange}
                            customClass={classes.datePickerInput}
                            labelClass={classes.label}
                            trailingIconVisible={true}
                            inputValue={getValues('pStart')}
                          ></FormInputDatePicker>
                        </LayoutGridItem>
                        <LayoutGridItem item xs={5}>
                          <FormInputDatePicker
                            name={'pEnd'}
                            label={'End Date'}
                            handleChange={handleEndDateChange}
                            readOnly={false}
                            customClass={classes.datePickerInput}
                            labelClass={classes.label}
                            trailingIconVisible={true}
                            inputValue={watch('pEnd')}
                          ></FormInputDatePicker>
                        </LayoutGridItem>
                      </LayoutGridItem>
                      <LayoutGridItem container>
                        <LayoutGridItem item xs={12}>
                          <Controller
                            name="pReason"
                            defaultValue={null}
                            control={control}
                            render={({ field: { ref, ...rest } }) => (
                              <TypeAhead
                                options={props.typeAheadOptions?.reasonForLeaving || []}
                                label="Reason for Leaving"
                                {...rest}
                                isError={errors['pReason']?.message || ''}
                              />
                            )}
                          />
                        </LayoutGridItem>
                      </LayoutGridItem>

                      {candidateManageEquipmentProcedure.enabled && (
                        <>
                          <LayoutGridItem item xs={12}>
                            <FormInputText
                              name="pnotes"
                              label={t('candidate.referenceGrid.equipmentProcedures')}
                              handleChange={handlePNotes}
                              inputValue={getValues('pnotes')}
                              message={errors['pnotes']?.message || ''}
                              multiline
                              rows={6}
                              spellCheck
                              inputLength={1000}
                              charCount={charCount}
                              customClass={classes.textBox}
                            />
                          </LayoutGridItem>
                        </>
                      )}
                    </LayoutGridItem>
                  </LayoutGridItem>
                </LayoutGrid>
              </LayoutGridItem>
            </>
          ) : (
            <>
              {errorMessageOpen && (
                <LayoutGridItem style={{ height: theme.spacing(17) }}>
                  <LayoutGrid
                    container
                    direction="row"
                    className={classes.root}
                    spacing={2}
                    style={{
                      backgroundColor: `${theme.palette.system.lightPaleRed}`,
                      padding: '10px',
                      paddingBottom: '15px',
                    }}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    xs={12}
                  >
                    <LayoutGridItem>
                      <LayoutGrid container direction="row" spacing={2}>
                        <LayoutGridItem>
                          <ErrorOutlineIcon style={{ color: `${theme.palette.system.errorIconRed}` }} />
                        </LayoutGridItem>
                        <LayoutGridItem>
                          <LayoutGrid container direction="column" spacing={0}>
                            <LayoutGridItem>
                              <span
                                style={{
                                  color: `${theme.palette.system.persianPlum}`,
                                  fontSize: '14px',
                                  fontWeight: 400,
                                }}
                              >
                                {t('candidate.dialogActions.datesOverlapMessage')}
                              </span>
                            </LayoutGridItem>
                            <LayoutGridItem>
                              <span
                                style={{
                                  color: `${theme.palette.system.persianPlum}`,
                                  fontSize: '14px',
                                  fontWeight: 400,
                                }}
                              >
                                {t('candidate.dialogActions.processConfirmation')}
                              </span>
                            </LayoutGridItem>
                          </LayoutGrid>
                        </LayoutGridItem>
                      </LayoutGrid>
                    </LayoutGridItem>
                    <LayoutGridItem>
                      <LayoutGrid
                        container
                        direction="row"
                        className={classes.root}
                        spacing={2}
                        style={{ paddingRight: '15px' }}
                      >
                        <LayoutGridItem>
                          <span
                            className={classes.buttonStyle}
                            onClick={() => {
                              setErrorMessageOpen(false);
                              setEnableSaveOnOverlap(false);
                            }}
                          >
                            {'No'}
                          </span>
                        </LayoutGridItem>
                        <LayoutGridItem>
                          <span className={classes.buttonStyle} onClick={() => yesAction()}>
                            {'Yes'}
                          </span>
                        </LayoutGridItem>
                      </LayoutGrid>
                    </LayoutGridItem>
                  </LayoutGrid>
                </LayoutGridItem>
              )}
              <LayoutGridItem item>
                <LayoutGrid
                  container
                  direction="row"
                  data-testid="container"
                  justifyContent="flex-start"
                  alignItems={'center'}
                  spacing={'12px'}
                  xs={10}
                >
                  <LayoutGridItem item xs={2.5}>
                    <FormInputDatePicker
                      name={'pStart'}
                      label={'Start Date *'}
                      handleChange={handleStartDateChange}
                      customClass={classes.datePickerInput}
                      trailingIconVisible={true}
                      inputValue={getValues('pStart')}
                    ></FormInputDatePicker>
                  </LayoutGridItem>
                  <LayoutGridItem item xs={2.5}>
                    <FormInputDatePicker
                      name={'pEnd'}
                      label={'End Date'}
                      handleChange={handleEndDateChange}
                      customClass={classes.datePickerInput}
                      trailingIconVisible={true}
                      inputValue={getValues('pEnd')}
                    ></FormInputDatePicker>
                  </LayoutGridItem>
                  <LayoutGridItem item xs={2.5}>
                    <FormInputCheckbox
                      name={`pOngoing`}
                      isChecked={pOngoing}
                      handleChecked={() => {
                        setPOngoing(!pOngoing);
                        if (!pOngoing == true) {
                          setValue('pEnd', null);
                          setEndValue(null);
                        }
                      }}
                      checkboxLabel={`Ongoing`}
                      isDisabled={false}
                      checkboxErrorName={`CheckboxError pOngoing`}
                    ></FormInputCheckbox>
                  </LayoutGridItem>
                </LayoutGrid>
              </LayoutGridItem>
              <LayoutGridItem
                item
                sx={{
                  marginTop: errors.pEnd || errors.Start ? '-36px' : '0px',
                }}
              >
                <LayoutGrid
                  container
                  direction="row"
                  data-testid="container"
                  justifyContent="flex-start"
                  alignItems={'center'}
                  spacing={'12px'}
                  xs={10}
                  sx={{
                    marginBottom: theme.spacing(4),
                  }}
                >
                  <LayoutGridItem item xs={4}>
                    <Controller
                      name="pCountry"
                      defaultValue={props.countriesData[0]}
                      control={control}
                      render={({ field: { ref, ...rest } }) => (
                        <TypeAhead
                          options={props.countriesData}
                          label="Country"
                          {...rest}
                          isError={!!errors['pCountry']}
                        />
                      )}
                    />
                  </LayoutGridItem>
                  <LayoutGridItem item xs={2.7}>
                    {watch('pCountry')?.id === CountryCodes.USA || watch('pCountry')?.id === CountryCodes.CANADA ? (
                      <Controller
                        name="pState"
                        defaultValue={null}
                        control={control}
                        render={({ field: { ref, ...rest } }) => (
                          <TypeAhead options={statesOptions || []} label="State" {...rest} isError={!!errors['pState']} />
                        )}
                      />
                    ) : (
                      <div className={classes.textFieldError}>
                        <FormInputTextField
                          name="pState"
                          label={'State'}
                          maxLength={100}
                          maxLengthErrorMessage={t(`global.textValidations.maxCharacterLimit`)}
                        />
                      </div>
                    )}
                  </LayoutGridItem>
                  <LayoutGridItem item xs={2.7}>
                    {watch('pCountry')?.id === CountryCodes.USA ? (
                      <Controller
                        defaultValue={null}
                        name="piCity"
                        control={control}
                        render={({ field: { ref, ...rest } }) => (
                          <TypeAhead
                            options={cityOptions || []}
                            label={
                              selectedCountry?.id === CountryCodes.USA && !!selectedState?.label ? 'City *' : 'City'
                            }
                            onInputChange={debounce(text => onTextSearch(text, 'c'), 1500)}
                            {...rest}
                          />
                        )}
                      />
                    ) : (
                      <div className={classes.textFieldError}>
                        <FormInputTextField
                          name="piCity"
                          label="City"
                          maxLength={100}
                          maxLengthErrorMessage={t(`global.textValidations.maxCharacterLimit`)}
                        />
                      </div>
                    )}
                  </LayoutGridItem>
                  <LayoutGridItem item xs={2.6}>
                    <div className={classes.textFieldError}>
                      <FormInputTextField
                        name="piZip"
                        label={'Zip Code'}
                        maxLength={5}
                        maxLengthErrorMessage={t(`global.textValidations.maxCharacterLimit`)}
                      />
                    </div>
                  </LayoutGridItem>
                </LayoutGrid>
              </LayoutGridItem>
            </>
          )}
        </LayoutGrid>

        <DialogActions
          {...props.dialogActionsProps}
          color={props.variant === 'blue' ? 'primary' : 'secondary'}
          classes={{
            ...props.dialogActionsProps?.classes,
            root: classes.actionButton,
          }}
        >
          {props.dialogActions.map((button, index) => (
            <div key={`generic-dialog-button-${index}`}>
              {!button.hidden &&
                (button.renderer ? (
                  button.renderer(button)
                ) : (
                  <GenericDialogButton {...button} onClick={button.useDialogOnCloseEvent ? onclose : button.onClick} />
                ))}
            </div>
          ))}
        </DialogActions>
      </div>
    </>
  );
};
